import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { BaseWidget, MetricCard } from './BaseWidget'
import { TasksIcon } from '../Icons'
import { API_BASE_URL } from '../../utils/api'

interface TaskOverviewWidgetProps {
  className?: string
  widgetId?: string
}

interface TaskMetrics {
  today: number
  thisWeek: number
  overdue: number
  totalActive: number
}

const TaskOverviewWidget: React.FC<TaskOverviewWidgetProps> = ({ className, widgetId }) => {
  const { t } = useTranslation()
  const [metrics, setMetrics] = useState<TaskMetrics | null>(null)
  const [loading, setLoading] = useState(true)
  const [isMinimal, setIsMinimal] = useState(false)

  // Detect widget height for minimal display
  useEffect(() => {
    const checkHeight = () => {
      const widgetElement = document.querySelector(`[data-widget-id="${widgetId || 'task_overview'}"]`)
      if (widgetElement) {
        const height = widgetElement.clientHeight
        setIsMinimal(height <= 230)
      }
    }
    
    checkHeight()
    const timer = setTimeout(checkHeight, 100)
    window.addEventListener('resize', checkHeight)
    const observer = new MutationObserver(checkHeight)
    const widgetElement = document.querySelector(`[data-widget-id="${widgetId || 'task_overview'}"]`)
    if (widgetElement) {
      observer.observe(widgetElement, { attributes: true, attributeFilter: ['style'] })
    }
    
    return () => {
      clearTimeout(timer)
      window.removeEventListener('resize', checkHeight)
      observer.disconnect()
    }
  }, [widgetId])

  const fetchTaskMetrics = async () => {
    try {
      const token = localStorage.getItem('token')
      if (!token) return

      const response = await fetch(`${API_BASE_URL}/api/dashboard/tasks`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      })

      if (response.ok) {
        const data: TaskMetrics = await response.json()
        setMetrics(data)
      }
    } catch (error) {
      console.error('Error fetching task metrics:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchTaskMetrics()
  }, [])

  return (
    <BaseWidget
      title={t('dashboard.widgets.taskOverview')}
      icon={<TasksIcon className="h-5 w-5" />}
      accent="tasks"
      variant="gradient"
      className={className}
      loading={loading || !metrics}
      minimalDisplay={isMinimal}
    >
      {isMinimal && metrics ? (
        // Minimal display - show only today and this week
        <div className="grid grid-cols-2 gap-3 h-full items-center">
          <div className="text-center">
            <div className="text-3xl font-bold text-white tabular-nums">{metrics.today || 0}</div>
            <div className="text-xs text-white/70">{t('dashboard.widgets.today')}</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-white tabular-nums">{metrics.thisWeek || 0}</div>
            <div className="text-xs text-white/70">{t('dashboard.widgets.thisWeek')}</div>
          </div>
        </div>
      ) : (
        // Full display
        <div className="grid grid-cols-2 gap-2">
          <MetricCard
            label={t('dashboard.widgets.today')}
            value={metrics?.today || 0}
            variant="gradient"
            className="p-2"
          />
          <MetricCard
            label={t('dashboard.widgets.thisWeek')}
            value={metrics?.thisWeek || 0}
            variant="gradient"
            className="p-2"
          />
          <MetricCard
            label={t('dashboard.widgets.overdue')}
            value={metrics?.overdue || 0}
            subValue={metrics?.overdue ? t('dashboard.widgets.needsAttention') : t('dashboard.widgets.allCaughtUp')}
            variant="gradient"
            className="p-2"
          />
          <MetricCard
            label={t('dashboard.widgets.totalActive')}
            value={metrics?.totalActive || 0}
            variant="gradient"
            className="p-2"
          />
        </div>
      )}
    </BaseWidget>
  )
}

export default TaskOverviewWidget